import React from 'react';
import QRCode from 'qrcode';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import { attributionLinkService } from '../services/attributionLinkService';
import { formatCompactNumber, formatPercent, formatDateTime } from '../utils/format';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import Toggle from '../components/ui/Toggle';
import Icon from '../components/ui/Icon';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Modal from '../components/ui/Modal';
import Tabs from '../components/ui/Tabs';
import Tooltip from '../components/ui/Tooltip';
import DateRangePicker from '../components/ui/DateRangePicker';
import EventBus from '../utils/EventBus';
import styles from './AttributionLinks.module.css';

const PAGE_SIZE = 20;

// Exact strings, mirroring attribution.validator.js's REDIRECTION_SCREENS —
// what pickmywork-android's redirection.js already knows how to route to.
// Display label is just the raw value title-cased for readability; the
// value itself (what actually gets sent/validated) stays byte-exact.
const REDIRECTION_SCREENS = [
  'JOB', 'JOB_CATEGORY', 'HOME', 'STAGES', 'WALLET_TRANSACTION', 'TASK_REPORT', 'JOB_TASK', 'TASK_WALLET',
  'REFERRAL', 'HOME_LEADERBOARD', 'TASK_LEADERBOARD', 'GAME_LEADERBOARD', 'PAYMENT_CERTIFICATE', 'TRAINING',
  'MY_TEAM', 'RECHARGE', 'RECHARGE_HISTORY', 'TICKET', 'HELP', 'OFFERS', 'SPIN_GAME', 'LEAD_RECOMMENDATION',
  'GENERAL', 'PERSONALINFO', 'MYPROFILE', 'SKILL', 'BANK', 'WALLET', 'CLEARTAX_TDS', 'BBPS', 'BBPS_HISTORY',
  'web', 'deal', 'leads', 'INVEST', 'GOLD_HISTORY', 'VIDEO',
];
function formatDay(isoString) {
  return isoString ? new Date(isoString).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';
}
function screenLabel(screen) {
  return screen.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
const REDIRECTION_SCREEN_OPTIONS = [
  { value: '', label: 'No specific screen (Home)' },
  ...REDIRECTION_SCREENS.map((s) => ({ value: s, label: screenLabel(s) })),
];

// The redirect lives on pmw-click-backend's own domain (see
// attributionRedirect.routes.js) — not the admin dashboard's own origin, and
// not behind /api/v1, so this is a plain hardcoded constant rather than
// something derived from apiClient's BASE_URL.
const REDIRECT_BASE_URL = 'https://dl.pickmywork.com/';

const EMPTY_FORM = {
  name: '', channel_type: 'other', // sent silently — see CHANNEL_TYPE_LABELS comment
  external_url: '',
  redirection_screen: '', redirection_entity_id: '', redirection_entity_type: '',
  utm_source: '', utm_medium: '', utm_campaign: '',
};

// Attribution methods are a fixed, small, well-known enum (unlike sources,
// which are open-ended) — worth their own color per method, same per-bar
// Cell-coloring pattern ABTests.jsx already uses, rather than one flat color.
const METHOD_LABELS = {
  token_match: 'Token Match',
  utm_params: 'UTM Params',
  store_referrer: 'Store Referrer',
  fingerprint_match: 'Fingerprint Match',
  unattributed: 'Unattributed',
};
const METHOD_COLORS = {
  token_match: '#c04040',
  utm_params: '#d97757',
  store_referrer: '#5a9e78',
  fingerprint_match: '#5080b8',
  unattributed: '#9c9c9c',
};

const NEW_VS_RETURNING_TOOLTIP =
  '"New" = this device linked to a real account within 2 days of install. The other bucket mixes genuine ' +
  "reinstalls with people who simply haven't logged in yet — the identity model can't tell those apart " +
  "without extra tracking, so it's labeled honestly rather than guessed at.";

class LinkRowSkeleton extends React.Component {
  render() {
    return (
      <tr className={styles.skRow}>
        <td><Skeleton variant="text" width={160} height={13} /></td>
        <td><Skeleton variant="text" width={140} height={13} /></td>
        <td><Skeleton variant="text" width={40} height={13} /></td>
        <td><Skeleton variant="text" width={40} height={13} /></td>
        <td><Skeleton variant="text" width={50} height={13} /></td>
        <td><Skeleton variant="text" width={110} height={13} /></td>
        <td><Skeleton variant="rect" width={36} height={20} style={{ borderRadius: 10 }} /></td>
        <td><Skeleton variant="rect" width={28} height={24} style={{ borderRadius: 6 }} /></td>
      </tr>
    );
  }
}

class AttributionLinks extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mainTab: 'app', appSubTab: 'links',
      loading: true, links: [], total: 0, page: 1,
      showCreateForm: false,
      form: { ...EMPTY_FORM }, formErrors: null, submitting: false,
      copiedId: null, confirmDelete: null, acting: false,
      qrLink: null, qrDataUrl: null,
      analyticsLoading: true, analytics: null,
      clickStatsLink: null, clickStats: null, clickStatsLoading: false,
    };
    this.load = this.load.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.loadAnalytics = this.loadAnalytics.bind(this);
  }

  componentDidMount() {
    this.load();
  }

  load() {
    const { mainTab, page } = this.state;
    this.setState({ loading: true });
    attributionLinkService.getAll({ page, limit: PAGE_SIZE, destination_type: mainTab })
      .then(({ data, meta }) => this.setState({ loading: false, links: data, total: meta.total }))
      .catch(() => this.setState({ loading: false }));
  }

  // DateRangePicker owns the range state and calls this on its own mount
  // and on every change (see its own doc comment) — same pattern
  // Analytics.jsx already uses, so switching to this tab always shows a
  // fresh, predictable range rather than a stale cached one from earlier
  // in the session.
  loadAnalytics(range) {
    this.setState({ analyticsLoading: true });
    attributionLinkService.getAnalytics(range)
      .then((analytics) => this.setState({ analyticsLoading: false, analytics }))
      .catch(() => this.setState({ analyticsLoading: false }));
  }

  setMainTab(mainTab) {
    this.setState({ mainTab, appSubTab: 'links', page: 1, showCreateForm: false, form: { ...EMPTY_FORM } }, this.load);
  }

  setAppSubTab(appSubTab) {
    this.setState({ appSubTab });
  }

  setPage(page) {
    this.setState({ page }, this.load);
  }

  setField(key, value) {
    this.setState((s) => ({ form: { ...s.form, [key]: value } }));
  }

  handleSubmit(e) {
    e.preventDefault();
    const { mainTab, form } = this.state;
    if (!form.name) {
      this.setState({ formErrors: 'Name is required.' });
      return;
    }
    if (mainTab === 'external' && !/^https?:\/\//i.test(form.external_url)) {
      this.setState({ formErrors: 'Destination URL is required and must start with http:// or https://.' });
      return;
    }
    // UTM tagging feeds the install waterfall/GA4/Analytics sub-tab for
    // 'app' links, so it's required there — 'external' links run none of
    // that, so it's just optional bookkeeping (see attribution.validator.js).
    if (mainTab === 'app' && (!form.utm_source || !form.utm_medium || !form.utm_campaign)) {
      this.setState({ formErrors: 'UTM source, medium, and campaign are all required for app links.' });
      return;
    }

    const payload = { name: form.name, channel_type: form.channel_type, destination_type: mainTab };
    if (form.utm_source) payload.utm_source = form.utm_source;
    if (form.utm_medium) payload.utm_medium = form.utm_medium;
    if (form.utm_campaign) payload.utm_campaign = form.utm_campaign;
    if (mainTab === 'external') {
      payload.external_url = form.external_url;
    } else {
      payload.redirection_screen = form.redirection_screen;
      payload.redirection_entity_id = form.redirection_entity_id;
    }

    this.setState({ submitting: true, formErrors: null });
    attributionLinkService.create(payload)
      .then(() => {
        this.setState({ submitting: false, showCreateForm: false, form: { ...EMPTY_FORM }, page: 1 });
        EventBus.emit('toast', { type: 'success', message: 'Attribution link created.' });
        this.load();
      })
      .catch((err) => {
        this.setState({ submitting: false, formErrors: err.message || 'Failed to create link.' });
      });
  }

  copyUrl(link) {
    navigator.clipboard.writeText(REDIRECT_BASE_URL + link.short_code).then(() => {
      this.setState({ copiedId: link.id });
      setTimeout(() => this.setState((s) => (s.copiedId === link.id ? { copiedId: null } : null)), 1500);
    });
  }

  showQr(link) {
    this.setState({ qrLink: link, qrDataUrl: null });
    QRCode.toDataURL(REDIRECT_BASE_URL + link.short_code, { width: 256, margin: 1 })
      .then((dataUrl) => this.setState({ qrDataUrl: dataUrl }))
      .catch(() => EventBus.emit('toast', { type: 'error', message: 'Failed to generate QR code.' }));
  }

  showClickStats(link) {
    this.setState({ clickStatsLink: link, clickStats: null, clickStatsLoading: true });
    attributionLinkService.getClickStats(link.id)
      .then((clickStats) => this.setState({ clickStatsLoading: false, clickStats }))
      .catch((err) => {
        this.setState({ clickStatsLoading: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load click stats.' });
      });
  }

  toggleActive(link) {
    attributionLinkService.update(link.id, { is_active: !link.is_active })
      .then(() => this.load())
      .catch((err) => EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to update link.' }));
  }

  act(promise, successMessage) {
    this.setState({ acting: true });
    promise
      .then(() => {
        this.setState({ acting: false, confirmDelete: null });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  // destinationType is the active tab ('app' or 'external') — each tab is
  // its own independently-paginated list (see attributionLink.model.js's
  // findAll), not a client-side filter over one combined list.
  renderLinksTab(destinationType) {
    const { loading, links, total, page, showCreateForm, form, formErrors, submitting, copiedId, confirmDelete, acting } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const isExternalTab = destinationType === 'external';

    return (
      <>
        <div className={styles.createBar}>
          <Button
            variant={showCreateForm ? 'secondary' : 'primary'}
            size="md"
            onClick={() => this.setState((s) => ({ showCreateForm: !s.showCreateForm, formErrors: null }))}
          >
            {showCreateForm ? 'Cancel' : isExternalTab ? '+ Create Short Link' : '+ Create Link'}
          </Button>
        </div>

        {showCreateForm && (
          <form className={styles.formCard} onSubmit={this.handleSubmit}>
            <div className={styles.formGrid}>
              <Input label="Link name" required value={form.name} onChange={(e) => this.setField('name', e.target.value)} placeholder="e.g. Instagram bio — Aug launch" />
              {isExternalTab ? (
                <Input
                  label="Destination URL"
                  required
                  value={form.external_url}
                  onChange={(e) => this.setField('external_url', e.target.value)}
                  placeholder="https://pickmywork.com/some-page"
                />
              ) : (
                <>
                  <Select
                    label="Opens to (optional)"
                    value={form.redirection_screen}
                    options={REDIRECTION_SCREEN_OPTIONS}
                    onChange={(v) => this.setField('redirection_screen', v)}
                  />
                  <Input label="Entity ID (optional)" value={form.redirection_entity_id} onChange={(e) => this.setField('redirection_entity_id', e.target.value)} placeholder="e.g. a job id" />
                </>
              )}
              <Input label={isExternalTab ? 'utm_source (optional)' : 'utm_source'} required={!isExternalTab} value={form.utm_source} onChange={(e) => this.setField('utm_source', e.target.value)} placeholder="meta" />
              <Input label={isExternalTab ? 'utm_medium (optional)' : 'utm_medium'} required={!isExternalTab} value={form.utm_medium} onChange={(e) => this.setField('utm_medium', e.target.value)} placeholder="cpc" />
              <Input label={isExternalTab ? 'utm_campaign (optional)' : 'utm_campaign'} required={!isExternalTab} value={form.utm_campaign} onChange={(e) => this.setField('utm_campaign', e.target.value)} placeholder="launch_q3" />
            </div>
            {formErrors && <div className={styles.formErr}>{formErrors}</div>}
            <Button variant="primary" size="md" loading={submitting} onClick={this.handleSubmit}>
              Save Link
            </Button>
          </form>
        )}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Name</th>
                {isExternalTab ? <th className={styles.th}>Destination URL</th> : <th className={styles.th}>Opens to</th>}
                <th className={styles.th}>Short URL</th>
                <th className={styles.th}>Clicks</th>
                {!isExternalTab && <th className={styles.th}>Installs</th>}
                {!isExternalTab && <th className={styles.th}>Conv. %</th>}
                <th className={styles.th}>Created</th>
                <th className={styles.th}>Active</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [0, 1, 2].map((i) => <LinkRowSkeleton key={i} />)
                : links.length === 0
                  ? (
                    <tr>
                      <td colSpan={isExternalTab ? 7 : 9}>
                        <EmptyState
                          icon="🔗"
                          title={isExternalTab ? 'No short links yet' : 'No attribution links yet'}
                          description={isExternalTab ? 'Create one to get a trackable short link to any URL.' : 'Create one to get a shareable click-to-install link.'}
                        />
                      </td>
                    </tr>
                  )
                  : links.map((link) => {
                      const convPct = link.click_count ? (link.install_count / link.click_count) * 100 : null;
                      return (
                        <tr key={link.id} className={styles.row}>
                          <td className={styles.tdName}>{link.name}</td>
                          <td className={styles.td}>
                            {isExternalTab ? (
                              <Tooltip content={link.external_url}>
                                <span className={styles.destUrl}>{link.external_url}</span>
                              </Tooltip>
                            ) : (
                              link.redirection_screen ? screenLabel(link.redirection_screen) : 'Home'
                            )}
                          </td>
                          <td className={styles.td}>
                            <span className={styles.shortUrl}>{REDIRECT_BASE_URL + link.short_code}</span>
                            <button className={styles.copyBtn} onClick={() => this.copyUrl(link)} title="Copy link">
                              <Icon name={copiedId === link.id ? 'check' : 'content_copy'} size={14} />
                            </button>
                            <button className={styles.copyBtn} onClick={() => this.showQr(link)} title="QR code">
                              <Icon name="qr_code_2" size={14} />
                            </button>
                            <button className={styles.copyBtn} onClick={() => this.showClickStats(link)} title="Click stats">
                              <Icon name="bar_chart" size={14} />
                            </button>
                          </td>
                          <td className={styles.tdNum}>{formatCompactNumber(link.click_count)}</td>
                          {!isExternalTab && <td className={styles.tdNum}>{formatCompactNumber(link.install_count)}</td>}
                          {!isExternalTab && <td className={styles.tdNum}>{formatPercent(convPct)}</td>}
                          <td className={styles.td}>{formatDateTime(link.created_on)}</td>
                          <td className={styles.td}>
                            <Toggle size="sm" checked={!!link.is_active} onChange={() => this.toggleActive(link)} />
                          </td>
                          <td className={styles.tdAct}>
                            <button className={styles.actBtn} onClick={() => this.setState({ confirmDelete: link })} title="Delete">
                              <Icon name="delete" size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>

        {!loading && links.length > 0 && (
          <div className={styles.pager}>
            <button className={styles.pagerBtn} disabled={page <= 1} onClick={() => this.setPage(page - 1)}>‹ Prev</button>
            <span className={styles.pagerInfo}>Page {page} of {totalPages} · {formatCompactNumber(total)} links</span>
            <button className={styles.pagerBtn} disabled={page >= totalPages} onClick={() => this.setPage(page + 1)}>Next ›</button>
          </div>
        )}

        <ConfirmDialog
          open={!!confirmDelete}
          title="Delete attribution link?"
          message={confirmDelete ? `"${confirmDelete.name}" will stop resolving — existing clicks/install data are kept, but the URL will no longer redirect.` : ''}
          confirmLabel="Delete"
          loading={acting}
          onConfirm={() => this.act(attributionLinkService.remove(confirmDelete.id), 'Attribution link deleted.')}
          onCancel={() => this.setState({ confirmDelete: null })}
        />
      </>
    );
  }

  renderAnalyticsTab() {
    const { analyticsLoading, analytics } = this.state;

    const totalInstalls = analytics ? analytics.byMethod.reduce((sum, r) => sum + Number(r.installs), 0) : 0;
    const topSource = analytics && analytics.bySource.length ? analytics.bySource[0] : null;
    const { new_users, returning_or_unidentified } = analytics ? analytics.newVsReturning : { new_users: 0, returning_or_unidentified: 0 };
    const maxCampaignInstalls = analytics && analytics.byCampaign.length
      ? Math.max(...analytics.byCampaign.map((r) => Number(r.installs)))
      : 0;

    return (
      <>
        <div className={styles.analyticsToolbar}>
          <DateRangePicker onChange={this.loadAnalytics} />
        </div>

        {analyticsLoading || !analytics ? (
          <div className={styles.statRow}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rect" width="100%" height={92} style={{ borderRadius: 10 }} />)}
          </div>
        ) : (
          <div className={styles.statRow}>
            <div className={styles.statCard}>
              <div className={[styles.statIco, styles.statIcoRed].join(' ')}><Icon name="download" size={18} /></div>
              <div className={styles.statBody}>
                <div className={styles.statVal}>{formatCompactNumber(totalInstalls)}</div>
                <div className={styles.statLbl}>Total installs</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={[styles.statIco, styles.statIcoBlue].join(' ')}><Icon name="trending_up" size={18} /></div>
              <div className={styles.statBody}>
                <div className={styles.statVal}>{topSource ? topSource.source : '—'}</div>
                <div className={styles.statLbl}>Top source{topSource ? ` · ${formatCompactNumber(topSource.installs)} installs` : ''}</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={[styles.statIco, styles.statIcoGreen].join(' ')}><Icon name="person_add" size={18} /></div>
              <div className={styles.statBody}>
                <div className={styles.statVal}>{formatCompactNumber(new_users)}</div>
                <div className={styles.statLbl}>
                  New signups
                  <Tooltip content={NEW_VS_RETURNING_TOOLTIP}>
                    <Icon name="info" size={13} style={{ marginLeft: 4, verticalAlign: 'middle', cursor: 'help' }} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={[styles.statIco, styles.statIcoGray].join(' ')}><Icon name="help" size={18} /></div>
              <div className={styles.statBody}>
                <div className={styles.statVal}>{formatCompactNumber(returning_or_unidentified)}</div>
                <div className={styles.statLbl}>
                  Returning / unidentified
                  <Tooltip content={NEW_VS_RETURNING_TOOLTIP}>
                    <Icon name="info" size={13} style={{ marginLeft: 4, verticalAlign: 'middle', cursor: 'help' }} />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        )}

        {analyticsLoading || !analytics ? (
          <div className={styles.chartsGrid}>
            <Skeleton variant="rect" width="100%" height={252} style={{ borderRadius: 10 }} />
            <Skeleton variant="rect" width="100%" height={252} style={{ borderRadius: 10 }} />
          </div>
        ) : (
          <div className={styles.chartsGrid}>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Installs by source</div>
              {analytics.bySource.length === 0 ? <EmptyState icon="📊" title="No data yet" /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics.bySource} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--t3)' }} allowDecimals={false} />
                    <YAxis type="category" dataKey="source" width={90} tick={{ fontSize: 11, fill: 'var(--t3)' }} />
                    <ChartTooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="installs" fill="var(--red)" radius={[0, 4, 4, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Installs by attribution method</div>
              {analytics.byMethod.length === 0 ? <EmptyState icon="📊" title="No data yet" /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics.byMethod} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--t3)' }} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="method"
                      width={110}
                      tick={{ fontSize: 11, fill: 'var(--t3)' }}
                      tickFormatter={(m) => METHOD_LABELS[m] || m}
                    />
                    <ChartTooltip
                      contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }}
                      formatter={(value) => [value, 'Installs']}
                      labelFormatter={(m) => METHOD_LABELS[m] || m}
                    />
                    <Bar dataKey="installs" radius={[0, 4, 4, 0]} maxBarSize={22}>
                      {analytics.byMethod.map((row) => (
                        <Cell key={row.method} fill={METHOD_COLORS[row.method] || 'var(--red)'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        <div className={styles.chartCard} style={{ marginTop: 16 }}>
          <div className={styles.chartTitle}>Top campaigns</div>
          {analyticsLoading || !analytics ? (
            <Skeleton variant="rect" width="100%" height={140} style={{ borderRadius: 8 }} />
          ) : analytics.byCampaign.length === 0 ? <EmptyState icon="📊" title="No campaign data yet" /> : (
            <div className={styles.campaignList}>
              {analytics.byCampaign.map((row) => {
                const pct = maxCampaignInstalls ? (Number(row.installs) / maxCampaignInstalls) * 100 : 0;
                return (
                  <div key={row.campaign + row.source} className={styles.campaignRow}>
                    <div className={styles.campaignInfo}>
                      <span className={styles.campaignName}>{row.campaign}</span>
                      <span className={styles.campaignSource}>{row.source}</span>
                    </div>
                    <div className={styles.campaignBarTrack}>
                      <div className={styles.campaignBarFill} style={{ width: `${Math.max(pct, 3)}%` }} />
                    </div>
                    <span className={styles.campaignCount}>{formatCompactNumber(row.installs)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  }

  render() {
    const { mainTab, appSubTab, qrLink, qrDataUrl, clickStatsLink, clickStats, clickStatsLoading } = this.state;
    const totalClicks = clickStats ? clickStats.daily.reduce((sum, r) => sum + Number(r.clicks), 0) : 0;

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Attribution Links</div>
            <div className={styles.pageSub}>Click-to-install links for paid ads and bio links, plus trackable short links to any URL</div>
          </div>
        </div>

        <Tabs
          tabs={[{ key: 'app', label: 'App Links' }, { key: 'external', label: 'External Links' }]}
          activeTab={mainTab}
          onChange={(key) => this.setMainTab(key)}
        >
          {mainTab === 'app' ? (
            // Analytics only ever reflects real app installs (see
            // getInstallAttributionStats) — nesting it under App Links
            // instead of a sibling tab makes that scope obvious instead of
            // implying it covers both link types.
            <Tabs
              variant="pill"
              tabs={[{ key: 'links', label: 'Links' }, { key: 'analytics', label: 'Analytics' }]}
              activeTab={appSubTab}
              onChange={(key) => this.setAppSubTab(key)}
            >
              {appSubTab === 'analytics' ? this.renderAnalyticsTab() : this.renderLinksTab('app')}
            </Tabs>
          ) : (
            this.renderLinksTab('external')
          )}
        </Tabs>

        <Modal
          open={!!qrLink}
          onClose={() => this.setState({ qrLink: null, qrDataUrl: null })}
          title="QR code"
          subtitle={qrLink ? REDIRECT_BASE_URL + qrLink.short_code : ''}
          width="360px"
        >
          <div className={styles.qrWrap}>
            {qrDataUrl ? (
              <>
                <img src={qrDataUrl} alt="QR code" width={256} height={256} />
                <a className={styles.qrDownload} href={qrDataUrl} download={qrLink ? `${qrLink.short_code}-qr.png` : 'qr.png'}>
                  Download PNG
                </a>
              </>
            ) : (
              <Skeleton variant="rect" width={256} height={256} />
            )}
          </div>
        </Modal>

        <Modal
          open={!!clickStatsLink}
          onClose={() => this.setState({ clickStatsLink: null, clickStats: null })}
          title="Click stats"
          subtitle={clickStatsLink ? `${clickStatsLink.name} · last 30 days` : ''}
          width="480px"
        >
          {clickStatsLoading || !clickStats ? (
            <Skeleton variant="rect" width="100%" height={220} style={{ borderRadius: 8 }} />
          ) : (
            <>
              <div className={styles.clickStatsTotal}>{formatCompactNumber(totalClicks)} clicks in the last 30 days</div>
              {clickStats.daily.length === 0 ? <EmptyState icon="📊" title="No clicks yet" /> : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={clickStats.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={formatDay} tick={{ fontSize: 10, fill: 'var(--t3)' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--t3)' }} />
                    <ChartTooltip
                      contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }}
                      labelFormatter={formatDay}
                    />
                    <Bar dataKey="clicks" fill="var(--red)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              {clickStats.byPlatform.length > 0 && (
                <div className={styles.clickStatsPlatforms}>
                  {clickStats.byPlatform.map((row) => (
                    <span key={row.platform} className={styles.clickStatsPlatformChip}>
                      {row.platform || 'unknown'} · {formatCompactNumber(row.clicks)}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>
      </>
    );
  }
}

export default AttributionLinks;
